/**
 * Configuration Settings Page
 * Manage admin commission and geofencing settings
 */

import { useState, useEffect } from 'react';
import { Save, Settings as SettingsIcon, Percent, MapPin, Calendar } from 'lucide-react';
import { realAPI } from '../api/realEndpoints';
import { useToastNotifications } from '../hooks/useToast';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import './ContentManagement.css';

export default function Settings() {
  const { success, error } = useToastNotifications();

  const [isLoading, setIsLoading] = useState(true);
  const [configValue, setConfigValue] = useState('');
  const [isAddingCommission, setIsAddingCommission] = useState(false);
  const [isAddingGeoRadius, setIsAddingGeoRadius] = useState(false);
  const [isAddingConfig, setIsAddingConfig] = useState(false);
  const [configFieldError, setConfigFieldError] = useState('');
  const [commissionPlaceholder, setCommissionPlaceholder] = useState('0');
  const [hasExistingConfig, setHasExistingConfig] = useState(false);
  const [geoRadius, setGeoRadius] = useState<string>('');
  const [geoRadiusError, setGeoRadiusError] = useState<string>('');
  const [bookingCancellationLimit, setBookingCancellationLimit] = useState<string>('');
  const [bookingCancellationLimitError, setBookingCancellationLimitError] = useState<string>('');
  const [bookingCancellationTimePeriod, setBookingCancellationTimePeriod] = useState<string>('');
  const [bookingRescheduleTimePeriod, setBookingRescheduleTimePeriod] = useState<string>('');
  const [bookingRescheduleTimePeriodError, setBookingRescheduleTimePeriodError] =
    useState<string>('');
  const [bookingRescheduleLimit, setBookingRescheduleLimit] = useState<string>('');
  const [bookingRescheduleLimitError, setBookingRescheduleLimitError] = useState<string>('');
  const [bookingCancellationTimePeriodError, setBookingCancellationTimePeriodError] =
    useState<string>('');
  const [bookingCancellationCharge, setBookingCancellationCharge] = useState<string>('');
  const [bookingCancellationChargeError, setBookingCancellationChargeError] = useState<string>('');
  const geoRadiusPlaceholder = '10';
  const bookingCancellationLimitPlaceholder = '5';
  const bookingCancellationTimePeriodPlaceholder = '30';
  const bookingCancellationChargePlaceholder = '0';
  const bookingRescheduleTimePeriodPlaceholder = '30';
  const bookingRescheduleLimitPlaceholder = '1';

  useEffect(() => {
    fetchCommissionConfig();
  }, []);

  const fetchCommissionConfig = async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!silent) {
      setIsLoading(true);
    }
    try {
      const data: any = await realAPI.paymentConfig.getConfig();
      if (!data) return;

      if (data?.error === 'notFound' || !data?.results || !Array.isArray(data.results)) {
        setCommissionPlaceholder('0');
        setConfigValue('0');
        setHasExistingConfig(false);
        setGeoRadius('');
        setBookingCancellationLimit('');
        setBookingCancellationTimePeriod('');
        setBookingRescheduleTimePeriod('');
        setBookingRescheduleLimit('');
        setBookingCancellationCharge('');
        return;
      }

      // Parse the results array to find all config values used by this form
      const commissionConfig = data.results.find((item: any) => item.key === 'commission');
      const geoRadiusConfig = data.results.find((item: any) => item.key === 'geofencing_radius');
      const bookingCancellationLimitConfig = data.results.find(
        (item: any) => item.key === 'booking_cancellation_limit'
      );
      const bookingCancellationTimePeriodConfig = data.results.find(
        (item: any) => item.key === 'booking_cancellation_time_period'
      );
      const bookingCancellationChargeConfig = data.results.find(
        (item: any) => item.key === 'booking_cancellation_charge'
      );
      const bookingRescheduleTimePeriodConfig = data.results.find(
        (item: any) => item.key === 'booking_reschedule_time_period'
      );
      const bookingRescheduleLimitConfig = data.results.find(
        (item: any) => item.key === 'booking_reschedule_limit'
      );

      // Handle commission config
      if (commissionConfig) {
        const commissionValue = commissionConfig.value;
        const normalized =
          commissionValue !== undefined &&
          commissionValue !== null &&
          String(commissionValue).trim() !== ''
            ? String(commissionValue)
            : '0';
        setCommissionPlaceholder(normalized);
        setConfigValue(normalized);
        setHasExistingConfig(true);
      } else {
        setCommissionPlaceholder('0');
        setConfigValue('0');
        setHasExistingConfig(false);
      }

      // Handle geofencing radius config
      if (geoRadiusConfig) {
        const radiusValue = geoRadiusConfig.value;
        const normalizedRadius =
          radiusValue !== undefined && radiusValue !== null && String(radiusValue).trim() !== ''
            ? String(radiusValue)
            : geoRadiusPlaceholder;
        setGeoRadius(normalizedRadius);
      } else {
        setGeoRadius('');
      }

      if (bookingCancellationLimitConfig) {
        const limitValue = bookingCancellationLimitConfig.value;
        const normalizedLimit =
          limitValue !== undefined && limitValue !== null && String(limitValue).trim() !== ''
            ? String(limitValue)
            : bookingCancellationLimitPlaceholder;
        setBookingCancellationLimit(normalizedLimit);
      } else {
        setBookingCancellationLimit('');
      }

      if (bookingCancellationTimePeriodConfig) {
        const periodValue = bookingCancellationTimePeriodConfig.value;
        const normalizedPeriod =
          periodValue !== undefined && periodValue !== null && String(periodValue).trim() !== ''
            ? String(periodValue)
            : bookingCancellationTimePeriodPlaceholder;
        setBookingCancellationTimePeriod(normalizedPeriod);
      } else {
        setBookingCancellationTimePeriod('');
      }

      if (bookingCancellationChargeConfig) {
        const chargeValue = bookingCancellationChargeConfig.value;
        const normalizedCharge =
          chargeValue !== undefined && chargeValue !== null && String(chargeValue).trim() !== ''
            ? String(chargeValue)
            : bookingCancellationChargePlaceholder;
        setBookingCancellationCharge(normalizedCharge);
      } else {
        setBookingCancellationCharge('');
      }

      if (bookingRescheduleTimePeriodConfig) {
        const periodValue = bookingRescheduleTimePeriodConfig.value;
        const normalizedPeriod =
          periodValue !== undefined && periodValue !== null && String(periodValue).trim() !== ''
            ? String(periodValue)
            : bookingRescheduleTimePeriodPlaceholder;
        setBookingRescheduleTimePeriod(normalizedPeriod);
      } else {
        setBookingRescheduleTimePeriod('');
      }

      if (bookingRescheduleLimitConfig) {
        const limitValue = bookingRescheduleLimitConfig.value;
        const normalizedLimit =
          limitValue !== undefined && limitValue !== null && String(limitValue).trim() !== ''
            ? String(limitValue)
            : bookingRescheduleLimitPlaceholder;
        setBookingRescheduleLimit(normalizedLimit);
      } else {
        setBookingRescheduleLimit('');
      }
    } catch (err) {
      console.error('Failed to fetch config:', err);
      setCommissionPlaceholder('0');
      setConfigValue('0');
      setHasExistingConfig(false);
      setGeoRadius('');
      setBookingCancellationLimit('');
      setBookingCancellationTimePeriod('');
      setBookingCancellationCharge('');
      setBookingRescheduleTimePeriod('');
      setBookingRescheduleLimit('');
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="w-full space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center">
          <SettingsIcon className="w-5 h-5 text-primary-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-ink">Configuration Settings</h1>
          <p className="text-sm text-ink-muted">Manage platform-wide settings and rules</p>
        </div>
      </div>

      <form
        onSubmit={async (e) => {
          e.preventDefault();

          let hasError = false;

          const commission = Number(configValue || commissionPlaceholder);
          if (Number.isNaN(commission) || commission < 1 || commission > 100) {
            setConfigFieldError('Commission must be between 1 and 100%');
            hasError = true;
          }

          const radius = Number(geoRadius || geoRadiusPlaceholder);
          if (Number.isNaN(radius) || radius < 1 || radius > 500) {
            setGeoRadiusError('Radius must be between 1 and 500 miles');
            hasError = true;
          }

          const cancellationLimit = Number(
            bookingCancellationLimit || bookingCancellationLimitPlaceholder
          );
          if (
            Number.isNaN(cancellationLimit) ||
            cancellationLimit < 1 ||
            cancellationLimit > 1000
          ) {
            setBookingCancellationLimitError('Cancellation limit must be between 1 and 1000');
            hasError = true;
          }

          const cancellationTimePeriod = Number(
            bookingCancellationTimePeriod || bookingCancellationTimePeriodPlaceholder
          );
          if (
            Number.isNaN(cancellationTimePeriod) ||
            cancellationTimePeriod < 1 ||
            cancellationTimePeriod > 365
          ) {
            setBookingCancellationTimePeriodError('Time period must be between 1 and 365 days');
            hasError = true;
          }

          const cancellationCharge = Number(
            bookingCancellationCharge || bookingCancellationChargePlaceholder
          );
          if (
            Number.isNaN(cancellationCharge) ||
            cancellationCharge < 0 ||
            cancellationCharge > 100000
          ) {
            setBookingCancellationChargeError('Cancellation charge must be between 0 and 100000');
            hasError = true;
          }

          const rescheduleTimePeriod = Number(
            bookingRescheduleTimePeriod || bookingRescheduleTimePeriodPlaceholder
          );
          if (
            Number.isNaN(rescheduleTimePeriod) ||
            rescheduleTimePeriod < 1 ||
            rescheduleTimePeriod > 168
          ) {
            setBookingRescheduleTimePeriodError(
              'Reschedule time period must be between 1 and 168 days'
            );
            hasError = true;
          }

          const rescheduleLimit = Number(
            bookingRescheduleLimit || bookingRescheduleLimitPlaceholder
          );
          if (Number.isNaN(rescheduleLimit) || rescheduleLimit < 1 || rescheduleLimit > 10) {
            setBookingRescheduleLimitError('Reschedule limit must be between 1 and 10');
            hasError = true;
          }

          if (hasError) return;

          try {
            setIsAddingConfig(true);

            await Promise.all([
              realAPI.paymentConfig.addConfig('commission', commission.toString()),
              realAPI.paymentConfig.addConfig('geofencing_radius', radius.toString()),
              realAPI.paymentConfig.addConfig(
                'booking_cancellation_limit',
                cancellationLimit.toString()
              ),
              realAPI.paymentConfig.addConfig(
                'booking_cancellation_time_period',
                cancellationTimePeriod.toString()
              ),
              realAPI.paymentConfig.addConfig(
                'booking_cancellation_charge',
                cancellationCharge.toString()
              ),
              realAPI.paymentConfig.addConfig(
                'booking_reschedule_time_period',
                rescheduleTimePeriod.toString()
              ),
              realAPI.paymentConfig.addConfig(
                'booking_reschedule_limit',
                rescheduleLimit.toString()
              ),
            ]);

            success(
              'Settings Updated',
              `Commission: ${commission}% | Radius: ${radius} miles | Cancellation: ${cancellationLimit} in ${cancellationTimePeriod} days | Charge: ${cancellationCharge} | Reschedule: ${rescheduleLimit} in ${rescheduleTimePeriod} days`
            );
            await fetchCommissionConfig({ silent: true });
          } catch (err) {
            console.error(err);
            error('Failed to update settings');
          } finally {
            setIsAddingConfig(false);
          }
        }}
        className="space-y-6"
      >
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {/* ── Admin Commission Card ── */}
          <Card padding="lg">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                <Percent className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-ink">Admin Commission</h3>
                <p className="text-xs text-ink-muted">Platform earnings from bookings</p>
              </div>
            </div>
            <div className="space-y-4">
              <Input
                id="commission-percent"
                label="Commission Percentage (%)"
                type="number"
                value={configValue}
                step={0.01}
                min={1}
                max={100}
                disabled={isAddingConfig}
                error={configFieldError}
                placeholder={commissionPlaceholder}
                onChange={(e) => {
                  let value = e.target.value;
                  if (value === '') {
                    setConfigValue('');
                    setConfigFieldError('');
                    return;
                  }
                  if (value.length > 1 && value.startsWith('0') && !value.startsWith('0.')) {
                    value = value.replace(/^0+/, '');
                  }
                  const numValue = Number(value);
                  if (Number.isNaN(numValue) || numValue < 0 || numValue > 100) return;
                  setConfigValue(value);
                  if (configFieldError) setConfigFieldError('');
                }}
                onBlur={() => {
                  if (!configValue) return;
                  if (Number(configValue) < 1) setConfigValue('1');
                }}
              />
              <div className="p-3 bg-primary-50 border border-primary-100 rounded-xl">
                <p className="text-xs text-primary-700 font-medium">
                  Example: For a £100 booking, the platform earns{' '}
                  <strong>
                    £
                    {(((Number(configValue || commissionPlaceholder) || 0) / 100) * 100).toFixed(2)}
                  </strong>
                </p>
              </div>
            </div>
          </Card>

          {/* ── Geofencing Card ── */}
          <Card padding="lg">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-ink">Service Geofencing Radius</h3>
                <p className="text-xs text-ink-muted">Maximum distance for service availability</p>
              </div>
            </div>
            <div className="space-y-4">
              <Input
                id="geo-radius"
                label="Geofencing Radius (Miles)"
                type="number"
                value={geoRadius}
                step={1}
                min={1}
                max={500}
                disabled={isAddingConfig}
                error={geoRadiusError}
                placeholder={geoRadiusPlaceholder}
                onKeyDown={(e) => {
                  if (['.', ',', 'e', '-'].includes(e.key)) e.preventDefault();
                }}
                onChange={(e) => {
                  let value = e.target.value;
                  if (value === '') {
                    setGeoRadius('');
                    setGeoRadiusError('');
                    return;
                  }
                  if (value.includes('.')) return;
                  if (value.length > 1 && value.startsWith('0')) value = value.replace(/^0+/, '');
                  const numValue = Number(value);
                  if (Number.isNaN(numValue) || numValue < 1 || numValue > 500) return;
                  setGeoRadius(value);
                  if (geoRadiusError) setGeoRadiusError('');
                }}
                onBlur={() => {
                  if (!geoRadius) return;
                  const v = Number(geoRadius);
                  if (v < 1) setGeoRadius('1');
                  if (v > 500) setGeoRadius('500');
                }}
              />
              <div className="p-3 bg-primary-50 border border-primary-100 rounded-xl">
                <p className="text-xs text-primary-700 font-medium">
                  Services within{' '}
                  <strong>{Number(geoRadius || geoRadiusPlaceholder || 0)} miles</strong> of the
                  customer will be shown.
                </p>
              </div>
            </div>
          </Card>

          {/* ── Booking Cancellation Card ── */}
          <Card padding="lg">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-4 h-4 text-orange-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-ink">Booking Cancellation Rules</h3>
                <p className="text-xs text-ink-muted">
                  Define limits and charges for cancellations
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Input
                  id="booking-cancellation-time-period"
                  label={<span className="text-base font-semibold">Time Period (Days)</span>}
                  type="number"
                  value={bookingCancellationTimePeriod}
                  step={1}
                  min={1}
                  max={365}
                  disabled={isAddingConfig}
                  error={bookingCancellationTimePeriodError}
                  placeholder={bookingCancellationTimePeriodPlaceholder}
                  onKeyDown={(e) => {
                    if (['.', ',', 'e', '-'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    let value = e.target.value;

                    if (value === '') {
                      setBookingCancellationTimePeriod('');
                      setBookingCancellationTimePeriodError('');
                      return;
                    }

                    if (value.includes('.')) return;

                    if (value.length > 1 && value.startsWith('0')) {
                      value = value.replace(/^0+/, '');
                    }

                    const numValue = Number(value);
                    if (Number.isNaN(numValue) || numValue < 1 || numValue > 365) return;

                    setBookingCancellationTimePeriod(value);
                    if (bookingCancellationTimePeriodError)
                      setBookingCancellationTimePeriodError('');
                  }}
                  onBlur={() => {
                    if (!bookingCancellationTimePeriod) return;
                    const v = Number(bookingCancellationTimePeriod);
                    if (v < 1) setBookingCancellationTimePeriod('1');
                    if (v > 365) setBookingCancellationTimePeriod('365');
                  }}
                />

                <Input
                  id="booking-cancellation-limit"
                  label={
                    <span className="text-base font-semibold">
                      Number of Booking Cancellations Allowed
                    </span>
                  }
                  type="number"
                  value={bookingCancellationLimit}
                  step={1}
                  min={1}
                  max={1000}
                  disabled={isAddingConfig}
                  error={bookingCancellationLimitError}
                  placeholder={bookingCancellationLimitPlaceholder}
                  onKeyDown={(e) => {
                    if (['.', ',', 'e', '-'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    let value = e.target.value;

                    if (value === '') {
                      setBookingCancellationLimit('');
                      setBookingCancellationLimitError('');
                      return;
                    }

                    if (value.includes('.')) return;

                    if (value.length > 1 && value.startsWith('0')) {
                      value = value.replace(/^0+/, '');
                    }

                    const numValue = Number(value);
                    if (Number.isNaN(numValue) || numValue < 1 || numValue > 1000) return;

                    setBookingCancellationLimit(value);
                    if (bookingCancellationLimitError) setBookingCancellationLimitError('');
                  }}
                  onBlur={() => {
                    if (!bookingCancellationLimit) return;
                    const v = Number(bookingCancellationLimit);
                    if (v < 1) setBookingCancellationLimit('1');
                    if (v > 1000) setBookingCancellationLimit('1000');
                  }}
                />

                <div className="md:col-span-2">
                  <Input
                    id="booking-cancellation-charge"
                    label={
                      <span className="text-base font-semibold">
                        Booking Cancellation Charge (%)
                      </span>
                    }
                    type="number"
                    value={bookingCancellationCharge}
                    step={0.01}
                    min={0}
                    max={100000}
                    disabled={isAddingConfig}
                    error={bookingCancellationChargeError}
                    placeholder={bookingCancellationChargePlaceholder}
                    onKeyDown={(e) => {
                      if ([',', 'e', '-'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      let value = e.target.value;

                      if (value === '') {
                        setBookingCancellationCharge('');
                        setBookingCancellationChargeError('');
                        return;
                      }

                      if (value.length > 1 && value.startsWith('0') && !value.startsWith('0.')) {
                        value = value.replace(/^0+/, '');
                      }

                      const numValue = Number(value);
                      if (Number.isNaN(numValue) || numValue < 0 || numValue > 100000) return;

                      setBookingCancellationCharge(value);
                      if (bookingCancellationChargeError) setBookingCancellationChargeError('');
                    }}
                    onBlur={() => {
                      if (!bookingCancellationCharge) return;
                      const v = Number(bookingCancellationCharge);
                      if (v < 0) setBookingCancellationCharge('0');
                      if (v > 100000) setBookingCancellationCharge('100000');
                    }}
                  />
                </div>
              </div>

              <div className="p-3 bg-primary-50 border border-primary-100 rounded-xl">
                <p className="text-xs text-primary-700 font-medium">
                  A customer can cancel up to{' '}
                  <strong>
                    {Number(bookingCancellationLimit || bookingCancellationLimitPlaceholder || 0)}
                  </strong>{' '}
                  bookings in{' '}
                  <strong>
                    {Number(
                      bookingCancellationTimePeriod || bookingCancellationTimePeriodPlaceholder || 0
                    )}
                  </strong>{' '}
                  days, and a charge of{' '}
                  <strong>
                    {Number(bookingCancellationCharge || bookingCancellationChargePlaceholder || 0)}
                    %
                  </strong>{' '}
                  applies beyond that.
                </p>
              </div>
            </div>
          </Card>

          {/* ── Booking Reschedule Card ── */}
          <Card padding="lg">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-4 h-4 text-orange-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-ink">Booking Reschedule Rules</h3>
                <p className="text-xs text-ink-muted">Define limits for booking rescheduling</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Allowed Within */}
                <Input
                  id="booking-reschedule-time-period"
                  label={<span className="text-base font-semibold">Allowed Within (Days)</span>}
                  type="number"
                  value={bookingRescheduleTimePeriod}
                  step={1}
                  min={1}
                  max={168}
                  disabled={isAddingConfig}
                  error={bookingRescheduleTimePeriodError}
                  placeholder={bookingRescheduleTimePeriodPlaceholder}
                  onKeyDown={(e) => {
                    if (['.', ',', 'e', '-'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    let value = e.target.value;

                    if (value === '') {
                      setBookingRescheduleTimePeriod('');
                      setBookingRescheduleTimePeriodError('');
                      return;
                    }

                    if (value.includes('.')) return;

                    if (value.length > 1 && value.startsWith('0')) {
                      value = value.replace(/^0+/, '');
                    }

                    const numValue = Number(value);
                    if (Number.isNaN(numValue) || numValue < 1 || numValue > 168) return;

                    setBookingRescheduleTimePeriod(value);

                    if (bookingRescheduleTimePeriodError) {
                      setBookingRescheduleTimePeriodError('');
                    }
                  }}
                  onBlur={() => {
                    if (!bookingRescheduleTimePeriod) return;

                    const v = Number(bookingRescheduleTimePeriod);

                    if (v < 1) setBookingRescheduleTimePeriod('1');
                    if (v > 168) setBookingRescheduleTimePeriod('168');
                  }}
                />

                {/* Reschedule Limit */}
                <Input
                  id="booking-reschedule-limit"
                  label={
                    <span className="text-base font-semibold">
                      Number of Reschedules Allowed Per Month
                    </span>
                  }
                  type="number"
                  value={bookingRescheduleLimit}
                  step={1}
                  min={1}
                  max={10}
                  disabled={isAddingConfig}
                  error={bookingRescheduleLimitError}
                  placeholder={bookingRescheduleLimitPlaceholder}
                  onKeyDown={(e) => {
                    if (['.', ',', 'e', '-'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    let value = e.target.value;

                    if (value === '') {
                      setBookingRescheduleLimit('');
                      setBookingRescheduleLimitError('');
                      return;
                    }

                    if (value.includes('.')) return;

                    if (value.length > 1 && value.startsWith('0')) {
                      value = value.replace(/^0+/, '');
                    }

                    const numValue = Number(value);
                    if (Number.isNaN(numValue) || numValue < 1 || numValue > 10) return;

                    setBookingRescheduleLimit(value);

                    if (bookingRescheduleLimitError) {
                      setBookingRescheduleLimitError('');
                    }
                  }}
                  onBlur={() => {
                    if (!bookingRescheduleLimit) return;

                    const v = Number(bookingRescheduleLimit);

                    if (v < 1) setBookingRescheduleLimit('1');
                    if (v > 10) setBookingRescheduleLimit('10');
                  }}
                />
              </div>

              {/* Summary */}
              <div className="p-3 bg-primary-50 border border-primary-100 rounded-xl">
                <p className="text-xs text-primary-700 font-medium">
                  Customers can reschedule their booking within{' '}
                  <strong>
                    {Number(bookingRescheduleTimePeriod || bookingRescheduleTimePeriodPlaceholder)}
                  </strong>{' '}
                  days, with a maximum of{' '}
                  <strong>
                    {Number(bookingRescheduleLimit || bookingRescheduleLimitPlaceholder)}
                  </strong>{' '}
                  reschedule per month.
                </p>
              </div>
            </div>
          </Card>
        </div>

        {/* ── Actions ── */}
        <div className="flex justify-end gap-3 pb-6">
          <Button
            type="button"
            variant="secondary"
            onClick={() => window.location.reload()}
            disabled={isAddingConfig}
          >
            Reset
          </Button>
          <Button type="submit" loading={isAddingConfig} leftIcon={<Save className="w-4 h-4" />}>
            {isAddingConfig ? 'Saving…' : 'Save Settings'}
          </Button>
        </div>
      </form>
    </div>
  );
}
